// Shared inline SVG icons. These markup fragments were previously duplicated
// across CopyableSecret, RegenerateButton, HistoryRow and CliTokensTab; keeping
// one copy each means a glyph tweak lands everywhere at once. All icons inherit
// `currentColor` and are `aria-hidden` — the surrounding button carries the
// accessible label.

interface IconProps {
  size?: number;
  className?: string;
}

const stroke = {
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// Clipboard outline — "copy to clipboard".
export function CopyIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" {...stroke} />
    </svg>
  );
}

// Checkmark — "copied!" confirmation.
export function CheckIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M20 6L9 17l-5-5" {...stroke} />
    </svg>
  );
}

// Chain link — "copy share link".
export function LinkIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" {...stroke} />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" {...stroke} />
    </svg>
  );
}

// Crossed chain link — "sharing off".
export function LinkOffIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M9 17H7a5 5 0 0 1 0-10h2" {...stroke} />
      <path d="M15 7h2a5 5 0 0 1 3.5 8.5" {...stroke} />
      <line x1="8" y1="2.5" x2="20" y2="21.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// Plus — "add" / "save".
export function PlusIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 5v14M5 12h14" {...stroke} />
    </svg>
  );
}

// Vertical three-dot "more" affordance.
export function KebabIcon({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

// Circular arrow — "regenerate".
export function RegenerateIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" {...stroke} />
      <path d="M21 3v5h-5" {...stroke} />
    </svg>
  );
}
